// Browser-side client for the tournament API server (see `server/index.ts`).
// The server owns PostgreSQL and runs the live cascades; this module is a thin
// typed `fetch` wrapper that the React state layer (`src/state/TournamentContext`)
// calls. Tournament routes live under `/api/tournaments`; item routes are scoped
// by tournament id. Mutations respond with the full tournament snapshot.
//
// The base URL defaults to `/api` (proxied to the API server by Vite in dev, or
// served same-origin in production) and can be overridden with
// `VITE_API_BASE_URL` for split deployments.

import type {
  Athlete,
  AthleteAccessPanel,
  AthleteAccessRelation,
  AthleteInput,
  AthleteInviteView,
  AthleteLinkView,
  AthleteProfile,
  AthleteSelfEdit,
  AthleteClubSummary,
  AthleteClubView,
  AttendanceMarkInput,
  AttendanceRecord,
  Company,
  CompanyPhoto,
  CompanySeatUsage,
  CompanyType,
  CurrentUser,
  FootballMatchEvent,
  GameScore,
  LanguageCode,
  ListMonkInstanceInput,
  ListMonkInstanceView,
  Location,
  Membership,
  MembershipInput,
  MessageTemplateInput,
  MessageTemplateView,
  PermissionCode,
  PermissionInfo,
  PlayerInput,
  Registration,
  RoleInfo,
  Sport,
  SportProfile,
  SportProfileInput,
  Team,
  TeamInput,
  TournamentPhoto,
  TournamentState,
  TournamentSummary,
  TournamentConfig,
  TrainingClass,
  TrainingClassCoach,
  TrainingClassEnrollment,
  TrainingClassInput,
  TrainingClassMemberInput,
  TrainingSession,
  TrainingSessionInput,
  UserAccount,
} from '../domain';
import { getToken, notifyUnauthorized, UnauthorizedError } from './authClient';
import { SseParser } from './sse';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

interface TournamentResponse {
  tournament: TournamentState | null;
}

interface TournamentsResponse {
  tournaments: TournamentSummary[];
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    // Surface auth failures distinctly and drop the whole app to the login
    // screen — covers a token that expired or a user blocked/deleted mid-session.
    if (res.status === 401) {
      notifyUnauthorized();
      throw new UnauthorizedError(detail || 'Authentication required.');
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

async function requestTournament(
  path: string,
  init?: RequestInit,
): Promise<TournamentState | null> {
  const body = await call<TournamentResponse>(path, init);
  return body.tournament;
}

function postJson(path: string, payload: unknown): Promise<TournamentState | null> {
  return requestTournament(path, { method: 'POST', body: JSON.stringify(payload) });
}

export interface GenerateRequest {
  name?: string;
  config: TournamentConfig;
  roster: PlayerInput[];
  preferLargerOnRemainderTwo?: boolean;
}

export interface CreateRequest {
  name?: string;
  companyId?: number;
  sportId?: string;
  description?: string;
  coverUrl?: string | null;
  startsAt?: number | null;
  acceptPublicRegistration?: boolean;
  registrationClosesAt?: number | null;
  config: TournamentConfig;
  roster?: PlayerInput[];
}

/** List every persisted tournament (lightweight summaries). */
export async function fetchTournaments(): Promise<TournamentSummary[]> {
  const body = await call<TournamentsResponse>('/tournaments');
  return body.tournaments;
}

interface CompaniesResponse {
  companies: Company[];
}

interface CompanyResponse {
  company: Company;
}

/** List every company on the platform (oldest first; the default company leads). */
export async function fetchCompanies(): Promise<Company[]> {
  const body = await call<CompaniesResponse>('/companies');
  return body.companies;
}

/**
 * Subscription seat usage for every company the caller may see — one entry per
 * company, so the companies list costs a single request whatever its length (see
 * `features/subscription-seats.md`).
 */
export async function fetchCompanySeats(): Promise<CompanySeatUsage[]> {
  const body = await call<{ seats: CompanySeatUsage[] }>('/companies/seats');
  return body.seats;
}

/**
 * Seat usage for one company. Used by the surfaces showing a single club, which
 * need the figure to be live right after an athlete is added or joins the
 * roster, rather than as of the last list load.
 */
export async function fetchCompanySeatUsage(
  companyId: number,
): Promise<CompanySeatUsage> {
  const body = await call<{ seats: CompanySeatUsage }>(`/companies/${companyId}/seats`);
  return body.seats;
}

/**
 * Create a company with the given display name, type and home location. The
 * type and location are fixed at creation and can never be changed afterwards.
 */
export async function createCompany(
  name: string,
  type: CompanyType,
  locationId: number,
): Promise<Company> {
  const body = await call<CompanyResponse>('/companies', {
    method: 'POST',
    body: JSON.stringify({ name, type, locationId }),
  });
  return body.company;
}

interface LocationsResponse {
  locations: Location[];
}

/** The location dictionary, for the company-create form's location picker. */
export async function fetchLocations(): Promise<Location[]> {
  const body = await call<LocationsResponse>('/locations');
  return body.locations;
}

/** Rename a company. */
export async function renameCompany(id: number, name: string): Promise<Company> {
  const body = await call<CompanyResponse>(`/companies/${id}/update`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
  return body.company;
}

/** Only the fields present change; the name is edited via `renameCompany`. */
export interface CompanyProfileRequest {
  description?: string;
  logoUrl?: string | null;
  defaultLanguage?: LanguageCode;
  // Public club home page preference (see `features/club-home.md`): whether the
  // page lists the club's organised tournaments. (Every company's home page is
  // public — there is no visibility flag.)
  showTournaments?: boolean;
}

/** Update a company's profile (markdown description and/or logo URL). */
export async function updateCompanyProfile(
  id: number,
  req: CompanyProfileRequest,
): Promise<Company> {
  const body = await call<CompanyResponse>(`/companies/${id}/profile`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  return body.company;
}

/** Delete a company (must own no tournaments); returns the refreshed list. */
export async function deleteCompany(id: number): Promise<Company[]> {
  const body = await call<CompaniesResponse>(`/companies/${id}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return body.companies;
}

/**
 * Mark a self-registered company as verified (VERIFY_COMPANY — Super Admin
 * territory). Idempotent; the server notifies the company's admins by email on
 * the transition (see `features/company-registration.md`).
 */
export async function verifyCompany(id: number): Promise<Company> {
  const body = await call<CompanyResponse>(`/companies/${id}/verify`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return body.company;
}

interface CompanyPhotosResponse {
  photos: CompanyPhoto[];
}

/** List a company's profile photos (newest first). */
export async function fetchCompanyPhotos(companyId: number): Promise<CompanyPhoto[]> {
  const body = await call<CompanyPhotosResponse>(`/companies/${companyId}/photos`);
  return body.photos;
}

/**
 * Upload one company profile photo (raw image bytes). The server processes it
 * synchronously (auto-orient → resize → WebP, no watermark) and responds with
 * the company's refreshed photo list. Throws on auth failure, an unconfigured
 * store, a rejected image, or the per-company cap.
 */
export async function uploadCompanyPhoto(
  companyId: number,
  blob: Blob,
): Promise<CompanyPhoto[]> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}/companies/${companyId}/photos`, {
    method: 'POST',
    headers: {
      'Content-Type': blob.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: blob,
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    if (res.status === 401) {
      notifyUnauthorized();
      throw new UnauthorizedError(detail || 'Authentication required.');
    }
    throw new Error(detail || `Upload failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as CompanyPhotosResponse;
  return body.photos;
}

/** Delete one company profile photo; returns the refreshed list. */
export async function deleteCompanyPhoto(
  companyId: number,
  photoId: string,
): Promise<CompanyPhoto[]> {
  const body = await call<CompanyPhotosResponse>(
    `/companies/${companyId}/photos/${encodeURIComponent(photoId)}/delete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return body.photos;
}

interface AthletesResponse {
  athletes: Athlete[];
}

interface AthleteResponse {
  athlete: Athlete;
}

/** List a company's athletes (coaches included; oldest first). */
export async function fetchAthletes(companyId: number): Promise<Athlete[]> {
  const body = await call<AthletesResponse>(`/companies/${companyId}/athletes`);
  return body.athletes;
}

/**
 * Create an athlete under a company. The server generates the personal number
 * and returns it on the created profile.
 */
export async function createAthlete(
  companyId: number,
  input: AthleteInput,
): Promise<Athlete> {
  const body = await call<AthleteResponse>(`/companies/${companyId}/athletes`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return body.athlete;
}

/** Replace an athlete's profile (the personal number and company never change). */
export async function updateAthlete(
  personalNumber: string,
  input: AthleteInput,
): Promise<Athlete> {
  const body = await call<AthleteResponse>(
    `/athletes/${encodeURIComponent(personalNumber)}/update`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return body.athlete;
}

/** Delete an athlete; returns the owning company's refreshed list. */
export async function deleteAthlete(personalNumber: string): Promise<Athlete[]> {
  const body = await call<AthletesResponse>(
    `/athletes/${encodeURIComponent(personalNumber)}/delete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return body.athletes;
}

/** A single athlete/coach by personal number. Company access gates the read. */
export async function fetchAthlete(personalNumber: string): Promise<Athlete> {
  const body = await call<AthleteResponse>(
    `/athletes/${encodeURIComponent(personalNumber)}`,
  );
  return body.athlete;
}

/**
 * The aggregated profile of one athlete/coach — identity, club, sport profiles,
 * teams and training — for the profile page. Company access gates the read.
 */
export async function fetchAthleteProfile(
  personalNumber: string,
): Promise<AthleteProfile> {
  const body = await call<{ profile: AthleteProfile }>(
    `/athletes/${encodeURIComponent(personalNumber)}/profile`,
  );
  return body.profile;
}

/**
 * Set whether the athlete's profile page is served on the public route (the
 * shareable link). Requires MANAGE_ATHLETES; returns the refreshed athlete.
 */
export async function setAthleteProfileVisibility(
  personalNumber: string,
  isPublic: boolean,
): Promise<Athlete> {
  const body = await call<AthleteResponse>(
    `/athletes/${encodeURIComponent(personalNumber)}/visibility`,
    { method: 'POST', body: JSON.stringify({ isPublic }) },
  );
  return body.athlete;
}

/**
 * A club's full roster: the athletes it owns *plus* the guests it carries (see
 * `features/athlete-clubs.md`). Distinct from `fetchAthletes`, which stays the
 * owned-only list the club edits through.
 */
export async function fetchClubRoster(companyId: number): Promise<Athlete[]> {
  const body = await call<{ athletes: Athlete[] }>(`/companies/${companyId}/roster`);
  return body.athletes;
}

/**
 * Add an existing athlete to this club's roster as a guest, by personal number.
 * Their profile stays owned — and editable — by their home club. Requires
 * MANAGE_ATHLETES; returns the refreshed roster.
 */
export async function joinClubRoster(
  companyId: number,
  personalNumber: string,
): Promise<Athlete[]> {
  const body = await call<{ athletes: Athlete[] }>(
    `/companies/${companyId}/roster/join`,
    { method: 'POST', body: JSON.stringify({ personalNumber }) },
  );
  return body.athletes;
}

/** Remove a guest from this club's roster (the home club cannot be left). */
export async function leaveClubRoster(
  companyId: number,
  personalNumber: string,
): Promise<Athlete[]> {
  const body = await call<{ athletes: Athlete[] }>(
    `/companies/${companyId}/roster/${encodeURIComponent(personalNumber)}/leave`,
    { method: 'POST' },
  );
  return body.athletes;
}

// --- Athlete access: the club's side ------------------------------------
// (see `features/athlete-access.md`)

/**
 * Who can sign in to this athlete's profile, and which invitations are still
 * outstanding. Company access gates the read; the response carries no token or
 * token hash — the server projects both away.
 */
export async function fetchAthleteAccess(
  personalNumber: string,
): Promise<AthleteAccessPanel> {
  return call<AthleteAccessPanel>(
    `/athletes/${encodeURIComponent(personalNumber)}/access`,
  );
}

/**
 * Invite an athlete (`SELF`) or their parent/carer (`GUARDIAN`) to sign in. The
 * emailed link verifies the address by being opened, so there is no separate
 * verification step. Requires MANAGE_ATHLETE_ACCESS.
 */
export async function inviteAthleteAccess(
  personalNumber: string,
  email: string,
  relation: AthleteAccessRelation,
): Promise<AthleteInviteView> {
  const body = await call<{ invite: AthleteInviteView }>(
    `/athletes/${encodeURIComponent(personalNumber)}/invite`,
    { method: 'POST', body: JSON.stringify({ email, relation }) },
  );
  return body.invite;
}

/**
 * Withdraw a grant (staff side: any grant on their own club's athlete).
 * Responds with the refreshed access panel.
 */
export async function revokeAthleteAccess(
  personalNumber: string,
  accessId: string,
): Promise<AthleteAccessPanel> {
  return call<AthleteAccessPanel>(
    `/athletes/${encodeURIComponent(personalNumber)}/access/${encodeURIComponent(accessId)}/revoke`,
    { method: 'POST' },
  );
}

/** Take back a still-unclaimed invitation (e.g. a mistyped address). */
export async function cancelAthleteInvite(
  personalNumber: string,
  email: string,
  relation: AthleteAccessRelation,
): Promise<AthleteAccessPanel> {
  return call<AthleteAccessPanel>(
    `/athletes/${encodeURIComponent(personalNumber)}/access/cancel`,
    { method: 'POST', body: JSON.stringify({ email, relation }) },
  );
}

// --- Athlete access: the athlete's own side -----------------------------
// Everything below is authorised by the caller's access grants alone — never by
// a permission or a company link, which an athlete account does not have.

/** The athlete profiles this account reaches (its own, and any child's). */
export async function fetchMyAthletes(): Promise<AthleteLinkView[]> {
  const body = await call<{ athletes: AthleteLinkView[] }>('/me/athletes');
  return body.athletes;
}

/** The full profile of an athlete this account reaches. */
export async function fetchMyAthleteProfile(
  personalNumber: string,
): Promise<AthleteProfile> {
  const body = await call<{ profile: AthleteProfile }>(
    `/me/athletes/${encodeURIComponent(personalNumber)}`,
  );
  return body.profile;
}

/**
 * Change the self-editable part of a profile — photos and bio. Identity fields
 * are club-managed and cannot be reached from here whatever is sent.
 */
export async function updateMyAthleteProfile(
  personalNumber: string,
  patch: AthleteSelfEdit,
): Promise<AthleteProfile> {
  const body = await call<{ profile: AthleteProfile }>(
    `/me/athletes/${encodeURIComponent(personalNumber)}/update`,
    { method: 'POST', body: JSON.stringify(patch) },
  );
  return body.profile;
}

/**
 * Every club this athlete trains at, each with their training frequency and next
 * occurrence (see `features/athlete-clubs.md`). The home club is one of these,
 * flagged `isHome`.
 */
export async function fetchMyClubs(
  personalNumber: string,
): Promise<AthleteClubSummary[]> {
  const body = await call<{ clubs: AthleteClubSummary[] }>(
    `/me/athletes/${encodeURIComponent(personalNumber)}/clubs`,
  );
  return body.clubs;
}

/**
 * One club as this athlete sees it: everything its public home page carries plus
 * the full roster (private profiles included), their own training at the club and
 * what is coming up. 403s for a club the athlete is not a member of.
 */
export async function fetchMyClub(
  personalNumber: string,
  companyId: number,
): Promise<AthleteClubView> {
  return call<AthleteClubView>(
    `/me/athletes/${encodeURIComponent(personalNumber)}/clubs/${companyId}`,
  );
}

/**
 * Who else can reach this profile, from the athlete's own side. Same projection
 * as the club's panel; gated on the caller's grant rather than company access.
 */
export async function fetchMyAthleteAccess(
  personalNumber: string,
): Promise<AthleteAccessPanel> {
  return call<AthleteAccessPanel>(
    `/me/athletes/${encodeURIComponent(personalNumber)}/access`,
  );
}

/** A guardian gives their child their own login, by inviting an address. */
export async function grantChildAccess(
  personalNumber: string,
  email: string,
): Promise<AthleteInviteView> {
  const body = await call<{ invite: AthleteInviteView }>(
    `/me/athletes/${encodeURIComponent(personalNumber)}/child-access`,
    { method: 'POST', body: JSON.stringify({ email }) },
  );
  return body.invite;
}

/**
 * Revoke a grant from the athlete's side: an adult athlete removing a guardian,
 * a guardian stepping down, or a guardian withdrawing their child's login.
 * Responds with the caller's refreshed access picture.
 */
export async function revokeMyAccess(accessId: string): Promise<AthleteLinkView[]> {
  const body = await call<{ athletes: AthleteLinkView[] }>(
    `/me/access/${encodeURIComponent(accessId)}/revoke`,
    { method: 'POST' },
  );
  return body.athletes;
}

/**
 * A company's teams together with their memberships (see
 * `features/teams-sports.md`). Every team/membership mutation responds with
 * the owning company's refreshed snapshot, which the UI renders directly.
 */
export interface TeamsSnapshot {
  teams: Team[];
  memberships: Membership[];
}

interface SportsResponse {
  sports: Sport[];
}

interface SportProfilesResponse {
  profiles: SportProfile[];
}

/** The platform's sport catalog (config included; seeded by migrations). */
export async function fetchSports(): Promise<Sport[]> {
  const body = await call<SportsResponse>('/sports');
  return body.sports;
}

/**
 * Every sport profile of a company's athletes. Read-only: profiles are
 * created directly in the database for now (see `docs/adding-sport-profiles.md`).
 */
export async function fetchSportProfiles(companyId: number): Promise<SportProfile[]> {
  const body = await call<SportProfilesResponse>(
    `/companies/${companyId}/sport-profiles`,
  );
  return body.profiles;
}

/**
 * Create a sport profile for an athlete (or revive a previously removed one
 * for the same sport). Returns the owning company's refreshed profile list.
 */
export async function createSportProfile(
  personalNumber: string,
  input: SportProfileInput,
): Promise<SportProfile[]> {
  const body = await call<SportProfilesResponse>(
    `/athletes/${encodeURIComponent(personalNumber)}/sport-profiles`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return body.profiles;
}

/** Replace a profile's attributes (the athlete and sport never change). */
export async function updateSportProfile(
  profileId: number,
  input: SportProfileInput,
): Promise<SportProfile[]> {
  const body = await call<SportProfilesResponse>(`/sport-profiles/${profileId}/update`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return body.profiles;
}

/**
 * Delete a sport profile. Refused (409) while team memberships still link to
 * it. Returns the owning company's refreshed profile list.
 */
export async function deleteSportProfile(profileId: number): Promise<SportProfile[]> {
  const body = await call<SportProfilesResponse>(`/sport-profiles/${profileId}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return body.profiles;
}

/** A company's teams with their memberships (oldest first). */
export function fetchTeams(companyId: number): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/companies/${companyId}/teams`);
}

/** Create a team under a company; the sport is fixed at creation. */
export function createTeam(companyId: number, input: TeamInput): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/companies/${companyId}/teams`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Rename a team (the company and sport never change). */
export function updateTeam(
  teamId: number,
  input: Pick<TeamInput, 'name'>,
): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/teams/${teamId}/update`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Delete a team together with its memberships. */
export function deleteTeam(teamId: number): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/teams/${teamId}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Add an athlete to a team. The server resolves the athlete's sport profile
 * for the team's sport; without one the request is refused.
 */
export function addTeamMember(
  teamId: number,
  input: MembershipInput,
): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Update a membership's tier / team role (the links never change). */
export function updateMembership(
  membershipId: number,
  input: MembershipInput,
): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/memberships/${membershipId}/update`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Remove a member from a team (the athlete can be re-added later). */
export function deleteMembership(membershipId: number): Promise<TeamsSnapshot> {
  return call<TeamsSnapshot>(`/memberships/${membershipId}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * A company's training classes with their coaches and enrolments (see
 * `features/training-classes.md`). Every class/coach/enrolment mutation
 * responds with the company's refreshed snapshot, which the UI renders directly.
 */
export interface TrainingClassesSnapshot {
  classes: TrainingClass[];
  coaches: TrainingClassCoach[];
  enrollments: TrainingClassEnrollment[];
}

/** One class's dated sessions with their attendance marks. */
export interface TrainingSessionsSnapshot {
  sessions: TrainingSession[];
  attendance: AttendanceRecord[];
}

/** A company's training classes with their coaches and enrolments (oldest first). */
export function fetchTrainingClasses(companyId: number): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/companies/${companyId}/training-classes`);
}

/** Create a training class under a company. */
export function createTrainingClass(
  companyId: number,
  input: TrainingClassInput,
): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/companies/${companyId}/training-classes`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Edit a class's name, description and schedule (the company never changes). */
export function updateTrainingClass(
  classId: number,
  input: TrainingClassInput,
): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/training-classes/${classId}/update`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Delete a class together with its coaches, enrolments and sessions. */
export function deleteTrainingClass(classId: number): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/training-classes/${classId}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Attach a coach (a company athlete marked as coach) to a class. */
export function addTrainingClassCoach(
  classId: number,
  input: TrainingClassMemberInput,
): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/training-classes/${classId}/coaches`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Detach a coach from its class. */
export function removeTrainingClassCoach(
  coachId: number,
): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/training-class-coaches/${coachId}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Enrol a company athlete into a class. */
export function addTrainingClassEnrollment(
  classId: number,
  input: TrainingClassMemberInput,
): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(`/training-classes/${classId}/enrollments`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Remove an athlete's enrolment from a class. */
export function removeTrainingClassEnrollment(
  enrollmentId: number,
): Promise<TrainingClassesSnapshot> {
  return call<TrainingClassesSnapshot>(
    `/training-class-enrollments/${enrollmentId}/delete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/** A class's dated sessions with their attendance. */
export function fetchTrainingSessions(
  classId: number,
): Promise<TrainingSessionsSnapshot> {
  return call<TrainingSessionsSnapshot>(`/training-classes/${classId}/sessions`);
}

/** Add a dated session to a class. */
export function createTrainingSession(
  classId: number,
  input: TrainingSessionInput,
): Promise<TrainingSessionsSnapshot> {
  return call<TrainingSessionsSnapshot>(`/training-classes/${classId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Delete a session (its attendance is hidden with it). */
export function deleteTrainingSession(
  sessionId: number,
): Promise<TrainingSessionsSnapshot> {
  return call<TrainingSessionsSnapshot>(`/training-sessions/${sessionId}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Mark attendance for a session (present/absent per enrolled athlete). */
export function markTrainingAttendance(
  sessionId: number,
  records: AttendanceMarkInput[],
): Promise<TrainingSessionsSnapshot> {
  return call<TrainingSessionsSnapshot>(`/training-sessions/${sessionId}/attendance`, {
    method: 'POST',
    body: JSON.stringify({ records }),
  });
}

interface UsersResponse {
  users: UserAccount[];
}

interface UserResponse {
  user: UserAccount;
}

export interface CreateUserRequest {
  email: string;
  name?: string;
  password: string;
  /** Preferred UI / email language; null or omitted follows the company default. */
  language?: LanguageCode | null;
  roleIds?: string[];
  permissionIds?: PermissionCode[];
  companyIds?: number[];
}

/**
 * Every field optional: only what is present changes. Email is immutable.
 * `blocked` carries the intent as a boolean; the server stores it as the
 * `blockedAt` timestamp returned on the account.
 */
export interface UpdateUserRequest {
  name?: string;
  password?: string;
  /** Preferred UI / email language; null clears it (follow company default). */
  language?: LanguageCode | null;
  blocked?: boolean;
  roleIds?: string[];
  permissionIds?: PermissionCode[];
  companyIds?: number[];
}

/**
 * The signed-in user's own account and derived access picture (effective
 * permissions + company scope). The UI renders from this; the server still
 * enforces every rule on each request.
 */
export async function fetchMe(): Promise<CurrentUser> {
  return call<CurrentUser>('/auth/me');
}

/**
 * Update the signed-in user's own name and/or preferred language (see
 * `features/user-settings.md`). Self-scoped on the server — the session decides
 * whose account this is — so it needs no user-management permission. An omitted
 * field is left unchanged; `language: null` follows the company default again.
 */
export async function updateOwnProfile(req: {
  name?: string;
  language?: LanguageCode | null;
}): Promise<UserAccount> {
  const body = await call<UserResponse>('/auth/me/profile', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  return body.user;
}

/**
 * Change the signed-in user's own password. The current password must be given
 * and must verify, so a leaked session token alone cannot rotate it.
 */
export async function changeOwnPassword(req: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await call<{ ok: true }>('/auth/me/password', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** List every platform user with their grants (user managers only). */
export async function fetchUsers(): Promise<UserAccount[]> {
  const body = await call<UsersResponse>('/users');
  return body.users;
}

/** Create a user with an initial set of grants. */
export async function createUser(req: CreateUserRequest): Promise<UserAccount> {
  const body = await call<UserResponse>('/users', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  return body.user;
}

/** Update a user's profile, password, blocked state and/or grants. */
export async function updateUser(
  id: string,
  req: UpdateUserRequest,
): Promise<UserAccount> {
  const body = await call<UserResponse>(`/users/${encodeURIComponent(id)}/update`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  return body.user;
}

/** Delete a user; returns the refreshed list. */
export async function deleteUser(id: string): Promise<UserAccount[]> {
  const body = await call<UsersResponse>(`/users/${encodeURIComponent(id)}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return body.users;
}

/** Every role with its bundled permissions, for the assignment UI. */
export async function fetchRoles(): Promise<RoleInfo[]> {
  const body = await call<{ roles: RoleInfo[] }>('/roles');
  return body.roles;
}

/** The permission catalog, for the assignment UI. */
export async function fetchPermissions(): Promise<PermissionInfo[]> {
  const body = await call<{ permissions: PermissionInfo[] }>('/permissions');
  return body.permissions;
}

// --- Messaging configuration (Super-Admin-only): ListMonk instances and the
// per-language transactional templates the passwordless-login email resolves.
// Instance responses never carry the secret token (`hasToken` reports whether
// one is stored). See `features/passwordless-login.md`.

/** Every configured ListMonk instance (token redacted), oldest first. */
export async function fetchListMonkInstances(): Promise<ListMonkInstanceView[]> {
  const body = await call<{ instances: ListMonkInstanceView[] }>('/messaging/instances');
  return body.instances;
}

/** Create a ListMonk instance; returns the created (token-redacted) view. */
export async function createListMonkInstance(
  input: ListMonkInstanceInput,
): Promise<ListMonkInstanceView> {
  const body = await call<{ instance: ListMonkInstanceView }>('/messaging/instances', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return body.instance;
}

/**
 * Update a ListMonk instance. Omit `token` to keep the stored one; pass a
 * non-empty `token` to rotate it. Returns the updated (token-redacted) view.
 */
export async function updateListMonkInstance(
  id: number,
  input: ListMonkInstanceInput,
): Promise<ListMonkInstanceView> {
  const body = await call<{ instance: ListMonkInstanceView }>(
    `/messaging/instances/${id}/update`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return body.instance;
}

/** Delete a ListMonk instance (and its templates); returns the refreshed list. */
export async function deleteListMonkInstance(
  id: number,
): Promise<ListMonkInstanceView[]> {
  const body = await call<{ instances: ListMonkInstanceView[] }>(
    `/messaging/instances/${id}/delete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return body.instances;
}

/** Every per-language template mapping, oldest first. */
export async function fetchMessageTemplates(): Promise<MessageTemplateView[]> {
  const body = await call<{ templates: MessageTemplateView[] }>('/messaging/templates');
  return body.templates;
}

/**
 * Create or re-point the template mapping for an (instance, purpose, language)
 * triple; returns the refreshed template list.
 */
export async function saveMessageTemplate(
  input: MessageTemplateInput,
): Promise<MessageTemplateView[]> {
  const body = await call<{ templates: MessageTemplateView[] }>('/messaging/templates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return body.templates;
}

/** Delete a template mapping; returns the refreshed list. */
export async function deleteMessageTemplate(
  id: number,
): Promise<MessageTemplateView[]> {
  const body = await call<{ templates: MessageTemplateView[] }>(
    `/messaging/templates/${id}/delete`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return body.templates;
}

interface RegistrationsResponse {
  registrations: Registration[];
}

/** List the public sign-ups for a tournament (organiser-only, newest first). */
export async function fetchRegistrations(id: string): Promise<Registration[]> {
  const body = await call<RegistrationsResponse>(
    `/tournaments/${encodeURIComponent(id)}/registrations`,
  );
  return body.registrations;
}

/** Approve a sign-up; adds a player to the draft roster. Returns the new state. */
export function approveRegistration(
  id: string,
  registrationId: string,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/registrations/approve`, {
    registrationId,
  });
}

/** Reject a sign-up (removing its player if it had been approved). */
export function rejectRegistration(
  id: string,
  registrationId: string,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/registrations/reject`, {
    registrationId,
  });
}

/** Load a single tournament by id, or null if it does not exist. */
export function fetchTournament(id: string): Promise<TournamentState | null> {
  return requestTournament(`/tournaments/${encodeURIComponent(id)}`);
}

/** Create a DRAFT tournament (name + config + optional initial roster). */
export function createTournament(req: CreateRequest): Promise<TournamentState | null> {
  return postJson('/tournaments', req);
}

/**
 * Upload an image (raw bytes) to `path` and resolve to its public URL. The
 * blob's MIME type is sent as the Content-Type so the server can validate and
 * name the object. Throws on auth failure or a rejected/unconfigured upload.
 */
async function uploadImage(path: string, blob: Blob): Promise<string> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': blob.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: blob,
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    if (res.status === 401) {
      notifyUnauthorized();
      throw new UnauthorizedError(detail || 'Authentication required.');
    }
    throw new Error(detail || `Upload failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { url: string };
  return body.url;
}

/** Upload a tournament cover image and resolve to its public URL. */
export function uploadCover(blob: Blob): Promise<string> {
  return uploadImage('/uploads/cover', blob);
}

/** Upload a company logo image and resolve to its public URL. */
export function uploadLogo(blob: Blob): Promise<string> {
  return uploadImage('/uploads/logo', blob);
}

/** Upload an athlete profile photo and resolve to its public URL. */
export function uploadAthletePhoto(blob: Blob): Promise<string> {
  return uploadImage('/uploads/athlete-photo', blob);
}

interface PhotosResponse {
  photos: TournamentPhoto[];
}

/** List every photo for a tournament (organiser view — all statuses, newest first). */
export async function fetchPhotos(id: string): Promise<TournamentPhoto[]> {
  const body = await call<PhotosResponse>(
    `/tournaments/${encodeURIComponent(id)}/photos`,
  );
  return body.photos;
}

/**
 * Upload a zip of JPEG photos. Two steps: ask the server for a short-lived,
 * direct-to-storage upload URL, then PUT the zip straight to storage (bypassing
 * our server / its request-size cap). Processing then happens asynchronously —
 * the caller polls `fetchPhotos` to watch the photos appear and become published.
 * Throws on auth failure, an unconfigured store, or a failed PUT.
 */
export async function uploadPhotoZip(id: string, zip: Blob): Promise<void> {
  const { url } = await call<{ url: string }>(
    `/tournaments/${encodeURIComponent(id)}/photos/upload-url`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  // PUT straight to storage. No Authorization header — the signed URL is the
  // capability — and the content type must match what the URL was signed for.
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/zip' },
    body: zip,
  });
  if (!res.ok) {
    throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
  }
}

/** Delete a single photo; returns the refreshed photo list. */
export async function deletePhoto(id: string, photoId: string): Promise<TournamentPhoto[]> {
  const body = await call<PhotosResponse>(
    `/tournaments/${encodeURIComponent(id)}/photos/delete`,
    { method: 'POST', body: JSON.stringify({ photoId }) },
  );
  return body.photos;
}

/** Set (or clear) a photo's caption; returns the refreshed photo list. */
export async function setPhotoComment(
  id: string,
  photoId: string,
  comment: string,
): Promise<TournamentPhoto[]> {
  const body = await call<PhotosResponse>(
    `/tournaments/${encodeURIComponent(id)}/photos/comment`,
    { method: 'POST', body: JSON.stringify({ photoId, comment }) },
  );
  return body.photos;
}

/** Build and persist a fresh, playable tournament from a roster + config. */
export function generateTournament(req: GenerateRequest): Promise<TournamentState | null> {
  return postJson('/tournaments/generate', req);
}

/** Append a player to a DRAFT tournament. */
export function addPlayer(id: string, input: PlayerInput): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/players/add`, input);
}

/** Remove a player from a DRAFT tournament. */
export function removePlayer(
  id: string,
  playerId: string,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/players/remove`, { playerId });
}

/** Edit a DRAFT roster entry (rename, or revise a team's member list). */
export function updatePlayer(
  id: string,
  playerId: string,
  update: { name?: string; members?: string[] },
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/players/update`, {
    playerId,
    ...update,
  });
}

/** Add a referee (DRAFT or IN_PROGRESS football tournament). */
export function addReferee(id: string, name: string): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/referees/add`, { name });
}

/** Remove a referee, clearing any match assignments they held. */
export function removeReferee(
  id: string,
  refereeId: string,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/referees/remove`, { refereeId });
}

/** Assign a referee to a match (cell or node), or clear with null. */
export function assignReferee(
  id: string,
  matchId: string,
  refereeId: string | null,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/referees/assign`, {
    matchId,
    refereeId,
  });
}

/**
 * Withdraw a player from an IN_PROGRESS tournament (e.g. an injury). Annuls their
 * group matches and reopens any bracket progress they held; returns the cascaded
 * state so the tournament can continue.
 */
export function withdrawPlayer(
  id: string,
  playerId: string,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/players/withdraw`, { playerId });
}

/** Build the group stage + bracket and move a DRAFT to IN_PROGRESS. */
export function startTournament(
  id: string,
  preferLargerOnRemainderTwo?: boolean,
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/start`, {
    preferLargerOnRemainderTwo,
  });
}

/** Lock an IN_PROGRESS tournament once the bracket has a champion. */
export function finishTournament(id: string): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/finish`, {});
}

/** Delete a tournament by id. */
export function deleteTournament(id: string): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/delete`, {});
}

/**
 * Record a group-stage cell result; returns the cascaded state. `final` marks
 * a football score as the final result and `events` replaces the match's
 * recorded goal/card events (both ignored for best-of sports; omitting
 * `events` keeps the stored list).
 */
export function updateCell(
  id: string,
  cellId: string,
  games: GameScore[],
  final?: boolean,
  events?: FootballMatchEvent[],
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/cells/update`, {
    cellId,
    games,
    final,
    events,
  });
}

/** Record a bracket node result; returns the cascaded state (see `updateCell`). */
export function updateNode(
  id: string,
  nodeId: string,
  games: GameScore[],
  final?: boolean,
  events?: FootballMatchEvent[],
): Promise<TournamentState | null> {
  return postJson(`/tournaments/${encodeURIComponent(id)}/nodes/update`, {
    nodeId,
    games,
    final,
    events,
  });
}

/** Callbacks for a tournament's live-updates stream. */
export interface TournamentEventHandlers {
  /** A fresh full snapshot was pushed. */
  onState?: (state: TournamentState) => void;
  /** The public sign-up queue changed (refetch it). */
  onRegistrations?: () => void;
  /** The tournament was deleted. */
  onDeleted?: () => void;
  /** The stream (re)connected — a good time to reconcile any missed change. */
  onOpen?: () => void;
}

// Reconnect backoff bounds for the live stream. Cloud Run caps request duration,
// so the stream is expected to drop periodically and reconnect; a stalled server
// backs off up to the ceiling instead of hot-looping.
const SSE_MIN_RETRY_MS = 1_000;
const SSE_MAX_RETRY_MS = 30_000;

/**
 * Subscribe to a tournament's live-updates stream (Server-Sent Events) and keep
 * it connected, reconnecting with capped backoff when it drops. Unlike the
 * browser's `EventSource`, this uses `fetch` so the bearer token travels in the
 * `Authorization` header — never in the URL, where it would land in access logs.
 *
 * Returns an unsubscribe function that aborts the connection and stops
 * reconnecting. Safe to call when logged out (it simply does nothing until a
 * fresh subscription is opened after login).
 */
export function subscribeTournamentEvents(
  id: string,
  handlers: TournamentEventHandlers,
): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let retryMs = SSE_MIN_RETRY_MS;

  const run = async (): Promise<void> => {
    while (!stopped) {
      const token = getToken();
      if (!token) return; // logged out; a new subscription opens after re-login.

      controller = new AbortController();
      try {
        const res = await fetch(
          `${BASE_URL}/tournaments/${encodeURIComponent(id)}/events`,
          {
            headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
            cache: 'no-store',
            signal: controller.signal,
          },
        );
        // Terminal statuses: retrying cannot recover, and looping would leave a
        // silently stale view open. 401 → the session is dead (log out); 403 →
        // access to this tournament was revoked; 404 → it was deleted. In the
        // 403/404 cases drop back to the list rather than reconnect forever.
        if (res.status === 401) {
          notifyUnauthorized();
          return;
        }
        if (res.status === 403 || res.status === 404) {
          handlers.onDeleted?.();
          return;
        }
        if (!res.ok || !res.body) {
          throw new Error(`Live stream failed: ${res.status}`);
        }
        retryMs = SSE_MIN_RETRY_MS; // connected; reset backoff.
        handlers.onOpen?.();
        await pump(res.body, handlers, () => stopped);
      } catch {
        // Network drop, abort, or non-OK status: fall through to backoff+retry.
      }
      if (stopped) return;
      await sleep(retryMs, () => stopped);
      retryMs = Math.min(retryMs * 2, SSE_MAX_RETRY_MS);
    }
  };

  void run();

  return () => {
    stopped = true;
    controller?.abort();
  };
}

/** Read an SSE response body to completion, dispatching each event. */
async function pump(
  body: ReadableStream<Uint8Array>,
  handlers: TournamentEventHandlers,
  isStopped: () => boolean,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      for (const evt of parser.push(decoder.decode(value, { stream: true }))) {
        dispatch(evt, handlers);
      }
      if (isStopped()) break;
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}

function dispatch(evt: { event: string; data: string }, handlers: TournamentEventHandlers): void {
  switch (evt.event) {
    case 'state': {
      try {
        handlers.onState?.(JSON.parse(evt.data) as TournamentState);
      } catch {
        // Ignore a malformed frame rather than tear down the stream.
      }
      break;
    }
    case 'registrations':
      handlers.onRegistrations?.();
      break;
    case 'deleted':
      handlers.onDeleted?.();
      break;
  }
}

/** Resolve after `ms`, or sooner if the subscription is torn down. */
function sleep(ms: number, isStopped: () => boolean): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    // Best-effort early exit; the caller re-checks `stopped` after we resolve.
    if (isStopped()) {
      clearTimeout(timer);
      resolve();
    }
  });
}
